import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useTRPCAPI, useTRPCClientAPI, useTRPCMutation } from '../../trpc/trpc';
import { useShowToast } from '../../redux/slices/toasts/hooks';
import { useApplyFreshUserToken } from '../team/useApplyFreshUserToken';
import { useSMLRouter } from '../../routing/sml/hooks';
import { SMLRoutes } from '../../routing/sml/SMLRoutes';
import { isNotAMemberError } from '../team/leaveErrorMessage';
import { workspaceMessages } from './workspaceMessages';

interface LeaveVariables {
  licenseId: number;
  isActive: boolean;
  name: string;
}

export interface LeaveWorkspaceState {
  /** `onSettled` runs after the call, with or without an error. The confirm modal closes on it. */
  leave: (workspace: { licenseId: number; isActive: boolean; name: string }, onSettled?: () => void) => void;
  pendingLicenseId: number | null;
  error: unknown;
  /** The name of the workspace of the last call, for the error message. */
  errorWorkspaceName: string;
}

/**
 * `team.leave({ licenseId })` (design: workspaces e2). A leave of the active workspace applies the
 * home token, opens Home and shows "You left {workspace}." A leave of another workspace only
 * loads the list again (decision S4, README decision 4).
 */
export function useLeaveWorkspace(): LeaveWorkspaceState {
  const intl = useIntl();
  const trpcAPI = useTRPCAPI();
  const trpcClient = useTRPCClientAPI();
  const applyToken = useApplyFreshUserToken();
  const router = useSMLRouter();
  const queryClient = useQueryClient();
  const { showSuccess } = useShowToast();
  const { mutate, isPending, variables, error } = useTRPCMutation({
    mutationFn: async ({ licenseId, isActive }: LeaveVariables) => {
      const { token } = await trpcClient.team.leave.mutate({ licenseId });
      if (isActive) {
        await applyToken(token);
      }
    },
    onSuccess: (_data: void, { isActive, name }: LeaveVariables) => {
      if (isActive) {
        router.replace(SMLRoutes.home);
        showSuccess(intl.formatMessage(workspaceMessages.left, { workspace: name }));
        return;
      }
      queryClient.invalidateQueries({ queryKey: trpcAPI.workspaces.list.queryKey() }).catch(console.error);
    },
    onError: (leaveError: unknown) => {
      // The person already left on another device, or was removed: the row must go from the list.
      if (isNotAMemberError(leaveError)) {
        queryClient.invalidateQueries({ queryKey: trpcAPI.workspaces.list.queryKey() }).catch(console.error);
      }
    },
  });
  const leave = useCallback(
    (workspace: LeaveVariables, onSettled?: () => void) => {
      mutate({ licenseId: workspace.licenseId, isActive: workspace.isActive, name: workspace.name }, { onSettled });
    },
    [mutate]
  );
  const pendingLicenseId = isPending ? (variables?.licenseId ?? null) : null;
  return { leave, pendingLicenseId, error, errorWorkspaceName: variables?.name ?? '' };
}
